import { useState } from "react";
import { motion } from "framer-motion";
import { X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatErr } from "../auth/AuthContext";
import { useLang } from "../i18n";

export const AuthModal = ({ onClose }) => {
  const { login, register, loginGoogle } = useAuth();
  const { t } = useLang();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(email, password, name);
      toast.success(t.auth_ok);
      if (mode === "register") setTimeout(() => toast.success(t.welcome_credits, { duration: 6000 }), 400);
      onClose();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Erro");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="auth-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-white border-[4px] border-black rounded-2xl shadow-brutal-lg p-6"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display font-bold uppercase text-3xl tracking-tight">
            {mode === "login" ? t.auth_login : t.auth_register}
          </h2>
          <button data-testid="auth-close" onClick={onClose} className="border-[2px] border-black rounded-lg p-1">
            <X size={20} strokeWidth={2.5} />
          </button>
        </div>

        <button
          data-testid="google-login-btn"
          onClick={loginGoogle}
          className="w-full flex items-center justify-center gap-2 bg-white border-[3px] border-black rounded-xl px-4 py-3 font-bold text-sm shadow-brutal transition-[transform,box-shadow] duration-100 hover:shadow-[2px_2px_0px_0px_#000] hover:translate-x-[4px] hover:translate-y-[4px] mb-4"
        >
          <img src="https://www.google.com/favicon.ico" alt="" className="w-5 h-5" /> {t.auth_google}
        </button>

        <div className="flex items-center gap-3 my-4">
          <div className="h-[2px] flex-1 bg-black/20" />
          <span className="text-xs font-bold uppercase tracking-widest text-[#666]">{t.auth_or}</span>
          <div className="h-[2px] flex-1 bg-black/20" />
        </div>

        <form onSubmit={submit} className="flex flex-col gap-3">
          {mode === "register" && (
            <input
              data-testid="auth-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t.auth_name}
              className="border-[3px] border-black rounded-xl px-4 py-3 font-medium focus:outline-none focus:ring-4 focus:ring-[#BD00FF]"
            />
          )}
          <input
            data-testid="auth-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t.auth_email}
            className="border-[3px] border-black rounded-xl px-4 py-3 font-medium focus:outline-none focus:ring-4 focus:ring-[#BD00FF]"
          />
          <input
            data-testid="auth-password"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t.auth_pass}
            className="border-[3px] border-black rounded-xl px-4 py-3 font-medium focus:outline-none focus:ring-4 focus:ring-[#BD00FF]"
          />
          <button
            data-testid="auth-submit"
            type="submit"
            disabled={loading}
            className="mt-1 inline-flex items-center justify-center gap-2 bg-[#00FFA3] border-[3px] border-black rounded-xl px-4 py-3 font-display font-bold uppercase text-lg tracking-tight shadow-brutal transition-[transform,box-shadow] duration-100 hover:shadow-[2px_2px_0px_0px_#000] hover:translate-x-[4px] hover:translate-y-[4px] disabled:opacity-70"
          >
            {loading ? <Loader2 size={20} className="animate-spin" /> : mode === "login" ? t.auth_submit_login : t.auth_submit_reg}
          </button>
        </form>

        <p className="text-center text-sm font-medium mt-4">
          {mode === "login" ? t.auth_no_acc : t.auth_has_acc}{" "}
          <button
            data-testid="auth-switch"
            onClick={() => setMode(mode === "login" ? "register" : "login")}
            className="font-bold underline"
          >
            {mode === "login" ? t.auth_go_reg : t.auth_go_login}
          </button>
        </p>
      </motion.div>
    </div>
  );
};
